/**
 * ReminderRulesManager - Componente genérico para gerenciar regras de lembrete
 * 
 * Usado tanto no módulo de Agendamento quanto no Financeiro.
 * Recebe o entity_type e renderiza listagem + form de criação/edição.
 * Inclui dialog de confirmação ao reativar regra com opção de backfill.
 * 
 * @version 2.0.0
 * RELEASE: 2026-03-29 v1.0.0 - Criação do componente genérico
 * RELEASE: 2026-03-30 v2.0.0 - Dialog de reativação com backfill + correção toggle
 */

import { useState, useCallback } from 'react';
import { Bell, Plus, Loader2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useReminderRules, type ReminderRule, type ReminderEntityType } from '@/hooks/useReminderRules';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { ReminderRuleCard } from './ReminderRuleCard';
import { ReminderRuleFormDialog } from './ReminderRuleFormDialog';

const logger = createLogger('ReminderRulesManager');

// ============================================================================
// PROPS
// ============================================================================

interface ReminderRulesManagerProps {
  /** Tipo de entidade (scheduling, cobranca, etc.) */
  entityType: ReminderEntityType;
  /** Título da seção */
  title?: string;
  /** Descrição da seção */
  description?: string;
  /** Permitir offsets positivos (após vencimento) */
  allowPositiveOffsets?: boolean;
  /** Limite máximo de regras */
  maxRules?: number;
  /** Chave de permissão para controle granular (view/create/edit/delete) */
  permissionKey?: string;
}

// ============================================================================
// CONSTANTES
// ============================================================================

const ENTITY_LABELS: Record<ReminderEntityType, { title: string; description: string }> = {
  scheduling: {
    title: 'Lembretes de Agendamento',
    description: 'Configure lembretes automáticos para eventos agendados.',
  },
  cobranca: {
    title: 'Régua de Cobrança',
    description: 'Configure lembretes antes e após o vencimento das cobranças.',
  },
  contrato: {
    title: 'Lembretes de Contrato',
    description: 'Configure lembretes para contratos próximos ao vencimento.',
  },
  crm: {
    title: 'Lembretes de CRM',
    description: 'Configure lembretes automáticos para deals e oportunidades.',
  },
  // 2026-10-02: lembretes de vistoria
  vistoria: {
    title: 'Lembretes de Vistoria',
    description: 'Configure lembretes por status para avisar o responsável pela vistoria.',
  },
};

// ============================================================================
// COMPONENTE
// ============================================================================

export function ReminderRulesManager({
  entityType,
  title,
  description,
  allowPositiveOffsets = false,
  maxRules = 20,
  permissionKey,
}: ReminderRulesManagerProps) {
  const { currentTenant } = useAuth();
  const { canCreate: checkCanCreate, canEdit: checkCanEdit, canDelete: checkCanDelete, isAdmin } = usePermissions();

  // Permissões granulares — se não informar permissionKey, libera tudo (backward compatible)
  const hasCreatePermission = !permissionKey || isAdmin || checkCanCreate(permissionKey);
  const hasEditPermission = !permissionKey || isAdmin || checkCanEdit(permissionKey);
  const hasDeletePermission = !permissionKey || isAdmin || checkCanDelete(permissionKey);
  const {
    rules,
    isLoading,
    upsertRule,
    toggleRule,
    deleteRule,
    isUpserting,
    isDeleting,
  } = useReminderRules(entityType);

  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<ReminderRule | null>(null);

  // Estado para dialog de reativação com backfill
  const [reactivateDialog, setReactivateDialog] = useState<{
    open: boolean;
    ruleId: string;
    applyBackfill: boolean;
  }>({ open: false, ruleId: '', applyBackfill: false });
  const [isBackfilling, setIsBackfilling] = useState(false);

  const labels = ENTITY_LABELS[entityType];
  const displayTitle = title || labels.title;
  const displayDescription = description || labels.description;

  /** Abrir dialog de criação */
  const handleCreate = () => {
    setEditingRule(null);
    setShowFormDialog(true);
  };

  /** Abrir dialog de edição */
  const handleEdit = (rule: ReminderRule) => {
    setEditingRule(rule);
    setShowFormDialog(true);
  };

  /**
   * Toggle ativo/inativo
   * Desativar: executa direto (cancela pendentes)
   * Reativar: abre dialog de confirmação com opção de backfill
   */
  const handleToggle = useCallback((ruleId: string, isActive: boolean) => {
    if (isActive) {
      // Reativar → mostrar dialog de confirmação com opção de backfill
      setReactivateDialog({ open: true, ruleId, applyBackfill: false });
    } else {
      // Desativar → executa direto
      toggleRule.mutate({ ruleId, isActive });
    }
  }, [toggleRule]);

  /** Confirmar reativação (com ou sem backfill) */
  const handleConfirmReactivate = useCallback(async () => {
    const { ruleId, applyBackfill } = reactivateDialog;
    
    // 1. Reativar a regra
    toggleRule.mutate({ ruleId, isActive: true });

    // 2. Backfill assíncrono se solicitado (fire-and-forget)
    if (applyBackfill && currentTenant?.id) {
      setIsBackfilling(true);
      toast.info('Aplicando regra a registros existentes em segundo plano...');
      logger.info('Iniciando backfill ao reativar regra', { ruleId });

      try {
        const { data, error } = await supabase.functions.invoke('backfill-reminders', {
          body: { rule_id: ruleId, tenant_id: currentTenant.id },
        });

        if (error) {
          logger.error('Erro no backfill ao reativar', { error: error.message });
          toast.warning('Regra reativada, mas houve falha ao aplicar em registros antigos.', { duration: 6000 });
        } else {
          const result = data as { total_processed?: number } | null;
          const count = result?.total_processed ?? 0;
          toast.success(`Regra aplicada a ${count} registro(s) existente(s)!`);
        }
      } catch (err) {
        logger.error('Erro inesperado no backfill ao reativar', { error: String(err) });
        toast.warning('Regra reativada, mas não foi possível aplicar a registros antigos.');
      } finally {
        setIsBackfilling(false);
      }
    }

    setReactivateDialog({ open: false, ruleId: '', applyBackfill: false });
  }, [reactivateDialog, toggleRule, currentTenant?.id]);

  /** Excluir regra */
  const handleDelete = (ruleId: string) => {
    deleteRule.mutate(ruleId);
  };

  /** Submit do formulário (criação ou edição) */
  const handleSubmit = (input: Parameters<typeof upsertRule.mutate>[0]) => {
    upsertRule.mutate(input, {
      onSuccess: () => setShowFormDialog(false),
    });
  };

  const canAddMore = rules.length < maxRules;

  return (
    <div className="space-y-4">
      {/* Header da seção */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Bell className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{displayTitle}</h3>
            <p className="text-xs text-muted-foreground">{displayDescription}</p>
          </div>
        </div>
        {hasCreatePermission && (
          <Button
            size="xs"
            onClick={handleCreate}
            disabled={!canAddMore}
            className="gap-1.5 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <Plus className="h-3.5 w-3.5" />
            Adicionar
          </Button>
        )}
      </div>

      {/* Lista de regras */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Carregando regras...
        </div>
      ) : rules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed border-border/50 rounded-lg bg-muted/10">
          <Bell className="w-8 h-8 text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">Nenhuma regra configurada</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Adicione regras para enviar lembretes automáticos.
          </p>
          {hasCreatePermission && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCreate}
              className="mt-3 gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              Criar primeira regra
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => (
            <ReminderRuleCard
              key={rule.id}
              rule={rule}
              onEdit={handleEdit}
              onToggle={handleToggle}
              onDelete={handleDelete}
              isToggling={toggleRule.isPending}
              isDeleting={isDeleting}
              canEditRule={hasEditPermission}
              canDeleteRule={hasDeletePermission}
            />
          ))}

          {/* Contador de regras */}
          <p className="text-[10px] text-muted-foreground text-right">
            {rules.length}/{maxRules} regras configuradas
          </p>
        </div>
      )}

      {/* Dialog de criação/edição */}
      <ReminderRuleFormDialog
        open={showFormDialog}
        onOpenChange={setShowFormDialog}
        entityType={entityType}
        editingRule={editingRule}
        onSubmit={handleSubmit}
        isSubmitting={isUpserting}
        allowPositiveOffsets={allowPositiveOffsets}
        existingRules={rules}
      />

      {/* Dialog de confirmação ao REATIVAR regra */}
      <AlertDialog
        open={reactivateDialog.open}
        onOpenChange={(open) => {
          if (!open) setReactivateDialog({ open: false, ruleId: '', applyBackfill: false });
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-primary" />
              Reativar regra de lembrete
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Ao reativar, <strong>novos registros</strong> passarão a receber lembretes
                automaticamente conforme esta regra.
              </p>
              <p className="text-xs text-muted-foreground">
                Registros criados enquanto a regra estava desativada <strong>não receberam lembretes</strong>.
                Você pode optar por aplicar a regra a esses registros agora.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Opção de backfill */}
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20">
            <Checkbox
              id="backfill-check"
              checked={reactivateDialog.applyBackfill}
              onCheckedChange={(checked) =>
                setReactivateDialog((prev) => ({ ...prev, applyBackfill: !!checked }))
              }
            />
            <Label htmlFor="backfill-check" className="text-xs leading-relaxed cursor-pointer">
              Aplicar regra a registros existentes aptos
              <span className="block text-[10px] text-muted-foreground mt-0.5">
                Gera lembretes para entidades cujo horário de disparo ainda não passou.
              </span>
            </Label>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReactivate}
              disabled={isBackfilling}
              className="gap-1.5"
            >
              {isBackfilling ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" />
              )}
              Reativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
